import React, { useContext, useEffect, useState } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Box, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as R from 'ramda';
import { MDialog } from 'common';

// Utils
import { capitalize } from 'utils/helpers/format';
import { createSnackButtons } from 'utils/helpers/function';
import { DRAWER_TYPES } from 'utils/constants';
import ViewPanel from 'feature/inventory-management/product-transfer-order/view-panel/ViewPanel.connector';

// Context
import { AuthContext } from 'authentication/AuthContext';
import { DrawerContext } from '../drawer/DrawerContext';

// Components
import actions from './actions';
import Message from './Message';

const ConfirmationDialog = props => {
  const { pathname } = useLocation();
  const {
    createSnack,
    currentPtoDialogueOpen,
    ptoNumber,
    ptoDetails,
    ptoDetailsPending,
    fetchNotifications,
    fetchOpenPTOs,
    fetchPtoDetails,
    fetchSearchPTOs,
    resetCurrentPtoDialog,
    resetCreatePto,
    dispatch,
    setCurrentPtoDialogueOpen,
  } = props;
  const [title, setTitle] = useState('');
  const [holdPending, setHoldPending] = useState(false);
  const { affiliateRoles, roles: userRoles } = useContext(AuthContext);
  const {
    componentProps: { refreshNotifications },
    setDrawerComponentProps,
  } = useContext(DrawerContext);
  const dialogOpen = !!currentPtoDialogueOpen;

  const { originator, recipient, transferDate, volume, product, location } = ptoDetails;

  const { action, data, pastTense, reset } = actions(currentPtoDialogueOpen, dispatch);
  const actionData = useSelector(data);
  const { error: errorObj, pending, response } = actionData || {};

  // when clicking on a button, first param will be event, which can be ignored
  // second param is if the data was sent and any open drawer should close
  const handleToggleClose = (__, isSent = false) => {
    reset();
    setCurrentPtoDialogueOpen({ type: '' });
    // allow dialog to fade out before resetting title
    setTimeout(() => {
      setTitle('');
      setHoldPending(false);
    }, 1000);
    // keep drawer open if the user did not confirm (back button was hit, backdrop was clicked)
    // when clicking on the backdrop, material UI sends a second param of 'backdropClick'
    if (!isSent || isSent === 'backdropClick') return;
    if (refreshNotifications) {
      const currentRoles = [...userRoles, ...affiliateRoles];
      fetchNotifications(currentRoles);
    }
    setDrawerComponentProps({
      Component: '',
      resetWizard: undefined,
      type: '',
    });
    pathname.includes('open') ? fetchOpenPTOs() : fetchSearchPTOs();
  };

  const sendAction = () => {
    setHoldPending(true);
    const params = { ProductTransferOrderId: ptoNumber };
    action(params);
  };

  useEffect(() => {
    if (errorObj?.error) {
      setHoldPending(false);
      createSnack({
        message: errorObj?.error,
        options: {
          action: createSnackButtons([
            {
              label: 'VIEW ORDER',
              onClick: () =>
                setDrawerComponentProps({
                  Component: drawerProps => <ViewPanel ptoNumber={ptoNumber} {...drawerProps} />,
                  resetWizard: resetCreatePto,
                  type: DRAWER_TYPES.viewPtoDetails,
                }),
            },
          ]),
          onClose: () => {
            resetCurrentPtoDialog();
            dispatch(reset());
          },
          variant: 'error',
        },
      });
    }
    if (response && !R.isEmpty(response)) {
      fetchPtoDetails({ productTransferOrderId: ptoNumber });
      createSnack({
        message: ptoNumber ? `Product Transfer Order ${ptoNumber} ${pastTense}` : '',
        options: {
          action: createSnackButtons([
            {
              label: 'VIEW ORDER',
              onClick: () =>
                setDrawerComponentProps({
                  Component: drawerProps => <ViewPanel ptoNumber={ptoNumber} {...drawerProps} />,
                  resetWizard: resetCreatePto,
                  type: DRAWER_TYPES.viewPtoDetails,
                }),
            },
          ]),
          onClose: () => {
            resetCurrentPtoDialog();
            dispatch(reset());
          },
        },
      });
      handleToggleClose(null, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorObj?.error, response]);

  useEffect(() => {
    ptoNumber && dialogOpen && fetchPtoDetails({ productTransferOrderId: ptoNumber });
  }, [dialogOpen, fetchPtoDetails, ptoNumber]);

  useEffect(() => {
    currentPtoDialogueOpen &&
      ptoNumber &&
      setTitle(`${capitalize(currentPtoDialogueOpen)} Order ${ptoNumber}?`);
  }, [currentPtoDialogueOpen, ptoNumber]);

  return (
    <MDialog
      centeredHeader
      header={title}
      leftButtonAction={handleToggleClose}
      leftButtonText="Back"
      onClose={handleToggleClose}
      open={dialogOpen}
      rightButtonAction={sendAction}
      rightButtonText="Confirm"
    >
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        // eslint-disable-next-line
        minHeight={{ xs: '266px', sm: '174px' }}
      >
        {ptoDetailsPending || pending || holdPending ? (
          <CircularProgress />
        ) : (
          <Box display="flex" flexDirection="column" justifyContent="center" width={1}>
            <Message
              location={location}
              originator={originator}
              product={product}
              recipient={recipient}
              transferDate={transferDate}
              volume={volume}
            />
          </Box>
        )}
      </Box>
    </MDialog>
  );
};

ConfirmationDialog.propTypes = {
  createSnack: func.isRequired,
  currentPtoDialogueOpen: string,
  dispatch: func.isRequired,
  fetchNotifications: func.isRequired,
  fetchOpenPTOs: func.isRequired,
  fetchPtoDetails: func.isRequired,
  fetchSearchPTOs: func.isRequired,
  ptoDetails: shape({
    location: string,
    originator: string,
    product: string,
    ptoNumber: number,
    recipient: string,
    transferDate: string,
    volume: string,
  }).isRequired,
  ptoDetailsPending: bool.isRequired,
  ptoNumber: number,
  resetCreatePto: func.isRequired,
  resetCurrentPtoDialog: func.isRequired,
  setCurrentPtoDialogueOpen: func.isRequired,
};

ConfirmationDialog.defaultProps = {
  currentPtoDialogueOpen: '',
  ptoNumber: null,
};

export default compose()(ConfirmationDialog);
